import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FaBars, FaChevronDown, FaChevronUp, FaEdit, FaPlus } from 'react-icons/fa';
import { List, arrayMove } from 'react-movable';
import FileUpload from '../../components/FileUpload';
import DeleteModal from '../../components/modals/DeleteModal';
import { apiService } from '../../services/apiService';
import SpinnerLoader from '../../utils/SpinnerLoader';
import { errorNotify, successNotify } from '../../utils/toast';
import { MdDeleteOutline } from 'react-icons/md';

const AppModule = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [currentMainModule, setCurrentMainModule] = useState(null);
    const [modalName, setModalName] = useState("");
    const [modalIcon, setModalIcon] = useState("");
    const [isSubModule, setIsSubModule] = useState(false);
    const [isPermission, setIsPermission] = useState(false);
    const [currentMenu, setCurrentMenu] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [expandedModules, setExpandedModules] = useState({});
    const [expandedMenus, setExpandedMenus] = useState({});
    const [iconFiles, setIconFiles] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchModules = async () => {
        setLoading(true);
        try {
            const res = await apiService.getAppModules();
            if (res?.data) {
                const sorted = [...res.data].sort((a, b) => a.order - b.order);
                const sortedWithSub = sorted.map(mod => ({
                    ...mod,
                    menus: mod.menus ? [...mod.menus].sort((a, b) => a.order_num - b.order_num).map(menu => ({
                        ...menu,
                        permissions: menu.permissions ? [...menu.permissions].sort((a, b) => a.order_num - b.order_num) : []
                    })) : []
                }));
                setItems(sortedWithSub);
            }
        } catch (error) {
            errorNotify(error || "Failed to fetch modules");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModules();
    }, []);

    const toggleExpand = (id) => {
        setExpandedModules(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleMenuExpand = (id) => {
        setExpandedMenus(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleReorder = async ({ oldIndex, newIndex }) => {
        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);

        const payload = {
            module_orders: newItems.map((item, index) => ({
                id: item.id,
                order: index
            }))
        };

        try {
            await apiService.changeAppModuleOrder(payload);
            successNotify("Order updated successfully");
        } catch (error) {
            errorNotify(error || "Failed to update order");
            fetchModules();
        }
    };

    const handleSubModuleReorder = async (mainModuleId, { oldIndex, newIndex }) => {
        const mainModuleIndex = items.findIndex(item => item.id === mainModuleId);
        if (mainModuleIndex === -1) return;

        const mainModule = items[mainModuleIndex];
        const newSubModules = arrayMove(mainModule.menus, oldIndex, newIndex);

        const newItems = [...items];
        newItems[mainModuleIndex] = { ...mainModule, menus: newSubModules };
        setItems(newItems);

        const payload = {
            sub_module_orders: newSubModules.map((item, index) => ({
                id: item.id,
                order: index
            }))
        };

        try {
            await apiService.changeSubModuleOrder(payload);
            successNotify("Sub-module order updated successfully");
        } catch (error) {
            errorNotify(error || "Failed to update sub-module order");
            fetchModules();
        }
    };

    const handlePermissionReorder = async (mainModuleId, subModuleId, { oldIndex, newIndex }) => {
        const mainModuleIndex = items.findIndex(item => item.id === mainModuleId);
        if (mainModuleIndex === -1) return;

        const mainModule = items[mainModuleIndex];
        const subModuleIndex = mainModule.menus.findIndex(menu => menu.id === subModuleId);
        if (subModuleIndex === -1) return;

        const subModule = mainModule.menus[subModuleIndex];
        const newPermissions = arrayMove(subModule.permissions, oldIndex, newIndex);

        const newSubModules = [...mainModule.menus];
        newSubModules[subModuleIndex] = { ...subModule, permissions: newPermissions };

        const newItems = [...items];
        newItems[mainModuleIndex] = { ...mainModule, menus: newSubModules };
        setItems(newItems);

        const payload = {
            permission_orders: newPermissions.map((item, index) => ({
                id: item.id,
                order: index
            }))
        };

        try {
            await apiService.changePermissionOrder(payload);
            successNotify("Permission order updated successfully");
        } catch (error) {
            errorNotify(error || "Failed to update permission order");
            fetchModules();
        }
    };

    const handleSubModuleMove = (mainModuleId, currentIndex, direction) => {
        const mainModule = items.find(item => item.id === mainModuleId);
        if (!mainModule || !mainModule.menus) return;

        const newIndex = currentIndex + direction;
        if (newIndex < 0 || newIndex >= mainModule.menus.length) return;

        handleSubModuleReorder(mainModuleId, { oldIndex: currentIndex, newIndex });
    };

    const handlePermissionMove = (mainModuleId, subModuleId, currentIndex, direction) => {
        const mainModule = items.find(item => item.id === mainModuleId);
        if (!mainModule || !mainModule.menus) return;

        const subModule = mainModule.menus.find(menu => menu.id === subModuleId);
        if (!subModule || !subModule.permissions) return;

        const newIndex = currentIndex + direction;
        if (newIndex < 0 || newIndex >= subModule.permissions.length) return;

        handlePermissionReorder(mainModuleId, subModuleId, { oldIndex: currentIndex, newIndex });
    };

    const handleSave = async () => {
        if (!modalName.trim()) return;

        try {
            setSaveLoading(true);
            if (isPermission) {
                if (currentItem) {
                    await apiService.updatePermission({
                        permission_id: currentItem.id,
                        module_id: currentMainModule.id,
                        modules_menu_id: currentMenu.id,
                        name: modalName,
                        order_num: currentItem.order_num
                    });
                    successNotify("Permission updated successfully");
                } else {
                    await apiService.createPermission({
                        module_id: currentMainModule.id,
                        modules_menu_id: currentMenu.id,
                        name: modalName
                    });
                    successNotify("Permission created successfully");
                }
            } else if (isSubModule) {
                const iconPath = iconFiles.length > 0 ? iconFiles[0].fileName : "";

                if (currentItem) {
                    await apiService.updateSubModule({
                        sub_module_id: currentItem.id,
                        module_id: currentMainModule?.id || currentItem.module_id,
                        order_num: currentItem.order,
                        name: modalName,
                        icon: iconPath
                    });
                    successNotify("Menu updated successfully");
                } else {
                    await apiService.createSubModule({
                        module_id: currentMainModule.id,
                        name: modalName,
                        icon: iconPath
                    });
                    successNotify("Menu created successfully");
                    if (currentMainModule) {
                        setExpandedModules(prev => ({ ...prev, [currentMainModule.id]: true }));
                    }
                }
            } else {
                const iconPath = iconFiles.length > 0 ? iconFiles[0].fileName : "";
                if (currentItem) {
                    await apiService.updateAppModule({
                        app_module_id: currentItem.id,
                        name: modalName,
                        icon: iconPath
                    });
                    successNotify("Module updated");
                } else {
                    await apiService.createAppModule({
                        name: modalName,
                        icon: iconPath
                    });
                    successNotify("Module created successfully");
                }
            }

            setShowModal(false);
            setModalName("");
            setModalIcon("");
            setIconFiles([]);
            setCurrentItem(null);
            setCurrentMainModule(null);
            setCurrentMenu(null);
            setIsSubModule(false);
            setIsPermission(false);
            fetchModules();
        } catch (error) {
            errorNotify(error || "Failed to save");
        } finally {
            setSaveLoading(false);
        }
    };

    const openCreateModal = () => {
        setCurrentItem(null);
        setCurrentMainModule(null);
        setCurrentMenu(null);
        setModalName("");
        setIconFiles([]);
        setIsSubModule(false);
        setIsPermission(false);
        setShowModal(true);
    };


    const openEditModal = (item) => {
        setCurrentItem(item);
        setCurrentMainModule(null);
        setCurrentMenu(null);
        setModalName(item.name);

        if (item.icon) {
            setIconFiles([{
                fileName: item.icon,
                originalName: item.icon,
                previewUrl: `${process.env.REACT_APP_API_FILES_URL}${item.icon}`,
                backend: true
            }]);
        } else {
            setIconFiles([]);
        }

        setIsSubModule(false);
        setIsPermission(false);
        setShowModal(true);
    };

    const confirmDelete = (type, item) => {
        setDeleteTarget({ type, id: item.id, name: item.name });
        setShowDeleteModal(true);
    };

    const handleDeleteExecute = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            if (deleteTarget.type === "module") {
                const res = await apiService.deleteModule(deleteTarget.id);
                if (res) {
                    successNotify("Module deleted successfully");
                    fetchModules();
                }
            } else if (deleteTarget.type === "menu") {
                const res = await apiService.deleteSubModule(deleteTarget.id);
                if (res) {
                    successNotify("Menu deleted successfully");
                    fetchModules();
                }
            } else if (deleteTarget.type === "permission") {
                const res = await apiService.deleteBusinessPermission(deleteTarget.id);
                if (res) {
                    successNotify("Permission deleted successfully");
                    fetchModules();
                }
            }
            setShowDeleteModal(false);
            setDeleteTarget(null);
        } catch (error) {
            errorNotify(error || "Failed to delete");
        } finally {
            setDeleteLoading(false);
        }
    };

    const openCreateSubParamModal = (mainModule) => {
        setCurrentItem(null);
        setCurrentMainModule(mainModule);
        setCurrentMenu(null);
        setModalName("");
        setModalIcon("");
        setIconFiles([]);
        setIsSubModule(true);
        setIsPermission(false);
        setShowModal(true);
    };

    const openEditSubParamModal = (subParam, mainModule) => {
        setCurrentItem(subParam);
        setCurrentMainModule(mainModule);
        setCurrentMenu(null);
        setModalName(subParam.name);

        if (subParam.icon) {
            setIconFiles([{
                fileName: subParam.icon,
                originalName: subParam.icon,
                previewUrl: `${process.env.REACT_APP_API_FILES_URL}${subParam.icon}`,
                backend: true
            }]);
        } else {
            setIconFiles([]);
        }

        setIsSubModule(true);
        setIsPermission(false);
        setShowModal(true);
    };

    const openCreatePermissionModal = (menu, mainModule) => {
        setCurrentItem(null);
        setCurrentMainModule(mainModule);
        setCurrentMenu(menu);
        setModalName("");
        setIsSubModule(false);
        setIsPermission(true);
        setShowModal(true);
    };

    const openEditPermissionModal = (permission, menu, mainModule) => {
        setCurrentItem(permission);
        setCurrentMainModule(mainModule);
        setCurrentMenu(menu);
        setModalName(permission.name);
        setIsSubModule(false);
        setIsPermission(true);
        setShowModal(true);
    };

    return (
        <div className="p-4" style={{ minHeight: '95vh' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 style={{ color: '#3D3F5C' }}>App Business Modules</h3>
                <Button variant="primary" onClick={openCreateModal} className="d-flex align-items-center gap-2">
                    <FaPlus /> Create New
                </Button>
            </div>

            <div style={{ backgroundColor: '#F3F4F6', padding: "10px", minHeight: '80vh' }}>
                {loading && items.length === 0 ? (
                    <SpinnerLoader />
                ) : (
                    <div style={{ maxWidth: '800px', margin: '0 0' }}>
                        <List
                            key={JSON.stringify({ expandedModules, expandedMenus })}
                            values={items}
                            onChange={handleReorder}
                            renderList={({ children, props }) => (
                                <ul {...props} style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                                    {children}
                                </ul>
                            )}
                            isDragged={false}
                            renderItem={({ value, props, isDragged }) => {
                                const { onMouseDown, onTouchStart, ...otherProps } = props;
                                return (
                                    <li
                                        {...otherProps}
                                        style={{
                                            ...otherProps.style,
                                            padding: '0',
                                            margin: '0 0 12px 0',
                                            backgroundColor: '#fff',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            boxShadow: isDragged ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                            listStyle: 'none',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <div style={{
                                            padding: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            backgroundColor: isDragged ? '#eef2ff' : '#fff',
                                            cursor: isDragged ? 'grabbing' : 'default'
                                        }}>
                                            <div className="d-flex align-items-center gap-3 flex-grow-1" onClick={() => toggleExpand(value.id)} style={{ cursor: 'pointer' }}>
                                                <div
                                                    style={{ color: '#9CA3AF', cursor: 'grab' }}
                                                    onMouseDown={onMouseDown}
                                                    onTouchStart={onTouchStart}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <FaBars />
                                                </div>
                                                {value.icon && (
                                                    <img
                                                        src={`${process.env.REACT_APP_API_FILES_URL}${value.icon}`}
                                                        alt=""
                                                        style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                                                    />
                                                )}
                                                <span style={{ fontWeight: 600, color: '#1F2937', fontSize: '16px' }}>{value.name}</span>

                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    className="d-flex align-items-center gap-1"
                                                    onClick={(e) => { e.stopPropagation(); openCreateSubParamModal(value); }}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onTouchStart={(e) => e.stopPropagation()}
                                                >
                                                    <FaPlus size={12} /> Sub
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    className="p-0 text-decoration-none"
                                                    onClick={(e) => { e.stopPropagation(); openEditModal(value); }}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onTouchStart={(e) => e.stopPropagation()}
                                                >
                                                    <FaEdit size={18} color="#6B7280" />
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    className="p-0 text-decoration-none"
                                                    onClick={(e) => { e.stopPropagation(); confirmDelete("module", value); }}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onTouchStart={(e) => e.stopPropagation()}
                                                >
                                                    <MdDeleteOutline size={20} color="red" />
                                                </Button>
                                            </div>
                                        </div>

                                        {
                                            (value.menus && value.menus.length > 0) &&
                                            <div style={{ padding: '0 16px 16px 48px', borderTop: '1px solid #f3f4f6' }}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onTouchStart={(e) => e.stopPropagation()}
                                            >
                                                <List
                                                    values={value.menus}
                                                    // onChange={({ oldIndex, newIndex }) => handleSubModuleReorder(value.id, { oldIndex, newIndex })}
                                                    renderList={({ children, props }) => (
                                                        <ul {...props} style={{ padding: 0, margin: '12px 0 0 0', listStyle: 'none' }}>
                                                            {children}
                                                        </ul>
                                                    )}
                                                    renderItem={({ value: subValue, index: subIndex, props: subProps, isDragged: isSubDragged }) => {
                                                        const { onMouseDown: subOnMouseDown, onTouchStart: subOnTouchStart, ...subOtherProps } = subProps;

                                                        console.log(subValue, "subValue")
                                                        return (
                                                            <li
                                                                {...subOtherProps}
                                                                style={{
                                                                    ...subOtherProps.style,
                                                                    padding: '12px',
                                                                    marginBottom: '8px',
                                                                    backgroundColor: isSubDragged ? '#f0f9ff' : '#f9fafb',
                                                                    border: '1px solid #e5e7eb',
                                                                    borderRadius: '6px',
                                                                    cursor: isSubDragged ? 'grabbing' : 'default',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'stretch'
                                                                }}
                                                            >
                                                                <div className='d-flex align-items-center justify-content-between w-100'>
                                                                    <div className="d-flex align-items-center gap-3" onClick={() => toggleMenuExpand(subValue.id)} style={{ cursor: 'pointer' }}>
                                                                        <div
                                                                            style={{ cursor: 'grab', display: 'flex' }}
                                                                            onMouseDown={subOnMouseDown}
                                                                            onTouchStart={subOnTouchStart}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <FaBars size={12} color="#9CA3AF" />
                                                                        </div>
                                                                        <div className='d-flex align-items-center gap-2'>
                                                                            {subValue.icon && (
                                                                                <img
                                                                                    src={`${process.env.REACT_APP_API_FILES_URL}${subValue.icon}`}
                                                                                    alt=""
                                                                                    style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                                                                                />
                                                                            )}
                                                                            <span style={{ fontWeight: 500, color: '#374151', fontSize: '14px' }}>{subValue.name}</span>
                                                                        </div>

                                                                    </div>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <div
                                                                            onClick={(e) => { e.stopPropagation(); handleSubModuleMove(value.id, subIndex, -1); }}
                                                                            style={{ cursor: 'pointer', padding: '2px' }}
                                                                        >
                                                                            <FaChevronUp color={subIndex === 0 ? "#ccc" : "#6B7280"} />
                                                                        </div>
                                                                        <div
                                                                            onClick={(e) => { e.stopPropagation(); handleSubModuleMove(value.id, subIndex, 1); }}
                                                                            style={{ cursor: 'pointer', padding: '2px' }}
                                                                        >
                                                                            <FaChevronDown color={subIndex === (value.menus.length - 1) ? "#ccc" : "#6B7280"} />
                                                                        </div>

                                                                        <Button
                                                                            variant="link"
                                                                            className="p-0 text-decoration-none"
                                                                            onClick={(e) => { e.stopPropagation(); openEditSubParamModal(subValue, value); }}
                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                            onTouchStart={(e) => e.stopPropagation()}
                                                                        >
                                                                            <FaEdit size={14} color="#9CA3AF" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="link"
                                                                            className="p-0 text-decoration-none"
                                                                            onClick={(e) => { e.stopPropagation(); confirmDelete("menu", subValue); }}
                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                            onTouchStart={(e) => e.stopPropagation()}
                                                                        >
                                                                            <MdDeleteOutline size={14} color="red" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="light"
                                                                            size="sm"
                                                                            className="d-flex align-items-center gap-1"
                                                                            onClick={(e) => { e.stopPropagation(); openCreatePermissionModal(subValue, value); }}
                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                            onTouchStart={(e) => e.stopPropagation()}
                                                                        >
                                                                            <FaPlus size={10} /> Perm
                                                                        </Button>
                                                                    </div>
                                                                </div>

                                                                {
                                                                    (subValue.permissions && subValue.permissions.length > 0) &&
                                                                    <div
                                                                        style={{ width: '100%', marginTop: '8px', paddingLeft: '24px', borderTop: '1px solid #eee' }}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                        onTouchStart={(e) => e.stopPropagation()}
                                                                    >
                                                                        <List
                                                                            values={subValue.permissions}
                                                                            // onChange={({ oldIndex, newIndex }) => handlePermissionReorder(value.id, subValue.id, { oldIndex, newIndex })}
                                                                            renderList={({ children, props }) => (
                                                                                <ul {...props} style={{ padding: 0, margin: '8px 0 0 0', listStyle: 'none' }}>
                                                                                    {children}
                                                                                </ul>
                                                                            )}
                                                                            renderItem={({ value: perm, index: permIndex, props: permProps, isDragged: isPermDragged }) => (
                                                                                <li
                                                                                    {...permProps}
                                                                                    style={{
                                                                                        ...permProps.style,
                                                                                        padding: '8px',
                                                                                        marginBottom: '4px',
                                                                                        backgroundColor: isPermDragged ? '#f0f9ff' : '#fff',
                                                                                        border: '1px solid #f0f0f0',
                                                                                        borderRadius: '4px',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'space-between',
                                                                                        fontSize: '13px',
                                                                                        boxShadow: isPermDragged ? '0 5px 10px rgba(0,0,0,0.1)' : 'none',
                                                                                        zIndex: isPermDragged ? 100 : 'auto'
                                                                                    }}
                                                                                >
                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                        <div
                                                                                            style={{ cursor: 'grab', display: 'flex' }}
                                                                                            onMouseDown={permProps.onMouseDown}
                                                                                            onTouchStart={permProps.onTouchStart}
                                                                                        >
                                                                                            <FaBars size={10} color="#ccc" />
                                                                                        </div>
                                                                                        <span style={{ color: '#555' }}>{perm.name}</span>
                                                                                    </div>

                                                                                    <div className='d-flex align-items-center gap-2'>
                                                                                        <div
                                                                                            onClick={(e) => { e.stopPropagation(); handlePermissionMove(value.id, subValue.id, permIndex, -1); }}
                                                                                            style={{ cursor: 'pointer', padding: '2px' }}
                                                                                        >
                                                                                            <FaChevronUp size={12} color={permIndex === 0 ? "#ccc" : "#6B7280"} />
                                                                                        </div>
                                                                                        <div
                                                                                            onClick={(e) => { e.stopPropagation(); handlePermissionMove(value.id, subValue.id, permIndex, 1); }}
                                                                                            style={{ cursor: 'pointer', padding: '2px' }}
                                                                                        >
                                                                                            <FaChevronDown size={12} color={permIndex === (subValue.permissions.length - 1) ? "#ccc" : "#6B7280"} />
                                                                                        </div>
                                                                                        <Button
                                                                                            variant="link"
                                                                                            className="p-0 text-decoration-none"
                                                                                            onClick={(e) => {
                                                                                                openEditPermissionModal(perm, subValue, value);
                                                                                            }}
                                                                                        >
                                                                                            <FaEdit size={12} color="#ccc" />
                                                                                        </Button>
                                                                                        <Button
                                                                                            variant="link"
                                                                                            className="p-0 text-decoration-none"
                                                                                            onClick={(e) => { e.stopPropagation(); confirmDelete("permission", perm); }}
                                                                                            onMouseDown={(e) => e.stopPropagation()}
                                                                                            onTouchStart={(e) => e.stopPropagation()}
                                                                                        >
                                                                                            <MdDeleteOutline size={12} color="red" />
                                                                                        </Button>
                                                                                    </div>
                                                                                </li>
                                                                            )}
                                                                        />
                                                                    </div>
                                                                }
                                                            </li>
                                                        );
                                                    }}
                                                />
                                            </div>
                                        }
                                    </li>
                                )
                            }}
                        />
                    </div>
                )}
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isPermission
                            ? (currentItem ? "Edit Permission" : "Create Permission")
                            : isSubModule
                                ? (currentItem ? "Edit Menu" : "Create Menu")
                                : (currentItem ? "Edit Module" : "Create Module")
                        }
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3 mt-2">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={modalName}
                                onChange={(e) => setModalName(e.target.value)}
                                placeholder="Enter name"
                                autoFocus
                            />
                        </Form.Group>
                        {!isPermission && (
                            <Form.Group className="mb-3">
                                <Form.Label>Icon</Form.Label>
                                <FileUpload
                                    files={iconFiles}
                                    setFiles={setIconFiles}
                                    destination="app-modules"
                                    maxFileSize={5}
                                />
                            </Form.Group>
                        )}

                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button disabled={saveLoading} variant="primary" onClick={handleSave}>
                        {saveLoading ? "Loading..." : "Save"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <DeleteModal
                open={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                }}
                onLogout={handleDeleteExecute}
                loading={deleteLoading}
                topHeading={`Delete ${deleteTarget?.type === "module"
                    ? "Module"
                    : deleteTarget?.type === "menu"
                        ? "Menu"
                        : "Permission"
                }`}
                description={`Are you sure you want to delete ${deleteTarget?.type === "module"
                    ? "module"
                    : deleteTarget?.type === "menu"
                        ? "menu"
                        : "permission"
                } "${deleteTarget?.name}"?`}
                subDescription={
                    deleteTarget?.type === "permission"
                        ? "This permission will be permanently removed from all roles."
                        : "This action cannot be undone and will permanently remove associated permissions."
                }
            />
        </div>
    );
};

export default AppModule;